import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MaterialModule } from './material.module';
import { NavmenuComponent } from './navmenu/navmenu.component';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalRedirectComponent,
} from '@azure/msal-angular';

import { RcjitComponent } from './rcjit/rcjit.component';
import { HomeComponent } from './home/home.component';
import { ErrorHandlerService } from './services/error-handler.service';
import { ValidationErrorComponent } from './validation/validation.component';
import { ErrorDialogComponent } from './dialog/error-dialog/error-dialog.component';
import { SuccessDialogComponent } from './dialog/success-dialog/success-dialog.component';
import { DeadletterComponent } from './deadletter/deadletter.component';
import { ReleaseReportComponent } from './releasereport/releasereport.component';
import { ReleaseNotesComponent } from './releasenotes/releasenotes.component';
import { ConfirmationDialogComponent } from './components/confirmation-dialog/confirmation-dialog.component';
import { ProjectGeneratorComponent } from './projectgenerator/project-generator.component';
import { AnnouncementsComponent } from './announcements/announcements.component';

import { HeaderComponent } from './header/header.component';
import { SpinnerComponent } from './spinner/spinner.component';
import { AnnouncementSubscriptionDialogComponent } from './announcement-subscription-dialog/announcement-subscription-dialog.component';
import { NewAnnouncementDialogComponent } from './new-announcement-dialog/new-announcement-dialog.component';
import { MarioComponent } from './mario/mario.component';
import { ReleaseHistoryDialogComponent } from './mario/release-history-dialog/release-history-dialog.component';
import { TaskDisplayComponent } from './mario/task-display/task-display.component';
import { WorkflowTaskComponent } from './mario/workflow-task/workflow-task.component';
import { VariableDisplayComponent } from './mario/variable-display/variable-display.component';
import { MarioHeaderComponent } from './mario/header/mario-header.component';
import { ReleasesComponent } from './releases/releases.component';
import { EditReleaseDialogComponent } from './dialog/edit-release/edit-release-dialog.component';
import { AddDescriptionDialogComponent } from './dialog/add-description/add-description-dialog.component';
import { MarkdownModule } from 'ngx-markdown';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { DateRangePickerComponent } from './date-range-picker/date-range-picker.component';
import { DeleteBranchPrComponent } from './delete-branch-pr/delete-branch-pr.component';
import { LogAnalyticsQueryComponent } from './components/log-analytics-query/log-analytics-query.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
  provideCharts,
  withDefaultRegisterables,
} from 'ng2-charts';
import { LaAlertsByTeamComponent } from './components/la-alerts-by-team/la-alerts-by-team.component';
import { FeatureFlagManagerComponent } from './feature-flags/feature-flag-manager.component';
import { TeamMetricsComponent } from './team-metrics/team-metrics.component';
import { ChangeFailRateMetricComponent } from './components/metrics/change-fail-rate-metric.component';
import { DeploymentFrequencyMetricComponent } from './components/metrics/deployment-frequency-metric.component';
import { PrTimeToReviewMetricComponent } from './components/metrics/pr-time-to-review-metric.component'; // Added import
import { PbiTimeByColumnMetricComponent } from './components/metrics/pbi-time-by-column-metric.component';
import { ClassicReleaseMigrationComponent } from './classic-release-migration/classic-release-migration.component';
import { RequestChangesDialogComponent } from './components/request-changes-dialog/request-changes-dialog.component';
import { MetricCardComponent } from './components/metric-card/metric-card.component';
import { AgGridModule } from 'ag-grid-angular';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { ReleaseSuccessFailureRenderer } from './releases/release-success-failure-renderer';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import { ReviewClassificationDisplayPipe } from './pipes/review-classification-display.pipe';
import { ReleasesByTeamComponent } from './releases/releases-by-team.component';
import { ReleasesPresentationComponent } from './releases/releases-presentation.component';
import { RetryInterceptor } from './interceptors/retry-interceptor';
import { MSALGuardConfigFactory, MSALInstanceFactory, MSALInterceptorConfigFactory } from './msalFunctions';
import { OnCallModule } from './oncall/oncall.module';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { UserProfileComponent } from './components/user-profile/user-profile.component';
import { ImpersonateUserComponent } from './components/impersonate-user/impersonate-user.component';
import { ReleaseEnvironmentService } from './services/release-environment.service';
import { CommonModule } from '@angular/common';
import { ToastrModule } from 'ngx-toastr';
import { OwnershipStatsComponent } from './ownership-stats/ownership-stats.component';
import { AlertsBreakdownComponent } from './components/alerts-breakdown/alerts-breakdown.component';

ModuleRegistry.registerModules([AllEnterpriseModule, AllCommunityModule]);
LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-074343}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{ADESA_US}_is_granted_a_{Multiple_Applications}_Developer_License_for_{20}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{2}_Production_Environments___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{18_March_2026}____[v3]_[01]_MTc3Mzc5MjAwMDAwMA==1fe48f11f70ad2d7b3f8f740b776b0b8'
);

const appRoutes: Routes = [
  {
    path: '',
    component: HomeComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'me',
    component: UserProfileComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'rcjit',
    component: RcjitComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'rcjit/:id',
    component: RcjitComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'delete-branch-pr',
    component: DeleteBranchPrComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'mario',
    component: MarioComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'projectgenerator',
    component: ProjectGeneratorComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'deadletter',
    component: DeadletterComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'releasereport',
    component: ReleaseReportComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'releases',
    component: ReleasesComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'releases/retro',
    component: ReleasesPresentationComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'releases/team/:teamname',
    component: ReleasesComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'releasenotes',
    component: ReleaseNotesComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'oncall',
    loadChildren: () => OnCallModule,
    canActivate: [MsalGuard],
  },
  {
    path: 'announcements',
    component: AnnouncementsComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'featureflags',
    component: FeatureFlagManagerComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'metrics',
    component: TeamMetricsComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'classic-release-migration',
    component: ClassicReleaseMigrationComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'ownership-stats',
    component: OwnershipStatsComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'alerts-breakdown',
    component: AlertsBreakdownComponent,
    canActivate: [MsalGuard],
  }
];

@NgModule({
  declarations: [
    AppComponent,
    NavmenuComponent,
    RcjitComponent,
    MarioComponent,
    MarioHeaderComponent,
    ReleaseHistoryDialogComponent,
    TaskDisplayComponent,
    WorkflowTaskComponent,
    VariableDisplayComponent,
    HomeComponent,
    ValidationErrorComponent,
    ErrorDialogComponent,
    SuccessDialogComponent,
    ProjectGeneratorComponent,
    DeadletterComponent,
    ReleaseReportComponent,
    ReleaseNotesComponent,
    ConfirmationDialogComponent,
    AnnouncementsComponent,
    HeaderComponent,
    SpinnerComponent,
    AnnouncementSubscriptionDialogComponent,
    NewAnnouncementDialogComponent,
    ReleasesComponent,
    EditReleaseDialogComponent,
    AddDescriptionDialogComponent,
    DateRangePickerComponent,
    DeleteBranchPrComponent,
    LogAnalyticsQueryComponent,
    ReviewClassificationDisplayPipe,
    ReleaseSuccessFailureRenderer,
    TeamMetricsComponent,
    ClassicReleaseMigrationComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot({
      timeOut: 5000,
      positionClass: 'toast-bottom-center',
      preventDuplicates: true,
      closeButton: true,
      progressBar: true,
    }),
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    ReleasesByTeamComponent,
    ReleasesPresentationComponent,
    LaAlertsByTeamComponent,
    OnCallModule,
    RouterModule.forRoot(appRoutes, {}),
    MarkdownModule.forRoot(),
    MatDatepickerModule,
    MatInputModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatExpansionModule,
    MatTabsModule,
    MatIconModule,
    MatRadioModule,
    MatCheckboxModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    AgGridModule,
    UserProfileComponent,
    ImpersonateUserComponent,
    MetricCardComponent,
    ChangeFailRateMetricComponent,
    DeploymentFrequencyMetricComponent,
    PrTimeToReviewMetricComponent,
    PbiTimeByColumnMetricComponent,
    RequestChangesDialogComponent,
    OwnershipStatsComponent,
    AlertsBreakdownComponent
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RetryInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideHttpClient(withInterceptorsFromDi()),
    provideCharts(withDefaultRegisterables()),
    ReleaseEnvironmentService
  ],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule {}
